/**
 * Shared UI primitives for the meta shell.
 *
 * Plain DOM factories, not components with lifecycles — every screen in Batch 2 is expected
 * to build its own view with these as building blocks, and to manage its own teardown the
 * same way `armoury.ts` already does. Tailwind utility classes (plus the painted-kit `@utility`
 * helpers in `kit.css` — kit-window, kit-plate, kit-header, kit-tab, kit-chip, kit-bar) only, per CLAUDE.md: no custom CSS class
 * names, so every visual rule stays greppable from the template literal that uses it.
 *
 * Every interactive element here meets the 44x44 CSS px minimum tap target and reacts to
 * `pointerdown`/`click` rather than hover, per the mobile-first rule — hover is allowed to
 * *decorate* (`hover:` classes) but nothing requires it to function.
 *
 * These are the leverage point for the "game, not a website" restyle: every screen already
 * routes through `button`, `card`, `badge`, etc., so a material applied once here reaches
 * all nine meta routes without touching a single route file.
 */
import { t } from '../i18n';
import { playSample } from './menu-sfx';
import { plateChrome, type ButtonSize, type ButtonVariant } from './plate';

export { plateChrome, plateContent } from './plate';

/**
 * The drawn icons under `public/game/ui/icons/`, by name.
 *
 * A closed union rather than a free string, for the same reason a map's `material` is a closed
 * enum: a typo'd path renders nothing at all (Vite's dev server answers a missing asset with
 * HTML and a 200), and "the icon silently did not appear" is the hardest kind of visual bug to
 * notice in a screenshot review.
 *
 * The art is generated, keyed to transparency and checked in — see `public/game/ASSETS.md` for
 * the prompts, which is what makes it re-makeable rather than a one-off nobody can reproduce.
 */
export type IconName =
  | 'play'
  | 'loadout'
  | 'armoury'
  | 'shop'
  | 'inventory'
  | 'career'
  | 'leaderboards'
  | 'quests'
  | 'settings'
  | 'credits'
  | 'premium'
  | 'xp'
  | 'kills'
  | 'headshot'
  | 'time'
  | 'rank'
  | 'bomb'
  | 'streak'
  | 'locked'
  | 'unlocked'
  | 'done'
  | 'add'
  | 'prev'
  | 'next'
  | 'star'
  | 'favourite'
  | 'tools'
  | 'close'
  | 'trophy'
  | 'crate'
  | 'bell'
  | 'gift'
  | 'shield'
  | 'squad'
  | 'sound'
  | 'logout'
  | 'ammo'
  | 'health'
  | 'grenade'
  | 'pin'
  | 'flag'
  | 'refresh'
  | 'info'
  | 'warning'
  | 'copy';

export interface IconOptions {
  /** Rendered box, in px. The source art is 192px (painted kit, 2026-10-12), so anything up to 96 stays crisp on a 2x screen. */
  size?: number;
  className?: string;
}

/**
 * One drawn icon.
 *
 * An `<img>`, not an inline SVG sprite and not a font: the art is raster (generated, then
 * keyed and trimmed), and a raster icon in an `<img>` is the one form the browser will decode
 * off the main thread and cache by URL. `alt=""` and `aria-hidden` because every icon in this
 * shell sits beside its own label — an icon that repeated the word next to it would make a
 * screen reader say everything twice.
 */
export function icon(name: IconName, opts: IconOptions = {}): HTMLImageElement {
  const el = document.createElement('img');
  el.src = `/game/ui/icons/${name}.webp`;
  el.alt = '';
  el.setAttribute('aria-hidden', 'true');
  el.draggable = false;
  const size = opts.size ?? 20;
  el.width = size;
  el.height = size;
  el.className = ['shrink-0 select-none', opts.className ?? ''].filter(Boolean).join(' ');
  return el;
}

/** The medal art tiers under `public/game/ui/badges/`. */
export type BadgeArt = 'bronze' | 'silver' | 'gold' | 'platinum' | 'diamond' | 'marksman' | 'demolition' | 'defuser' | 'streak';

/**
 * An achievement medal.
 *
 * Bigger and louder than `icon` on purpose: a medal is the reward itself, and the whole point
 * of the achievements screen is that the picture is worth looking at. Dimmed rather than
 * hidden when `earned` is false — a locked medal you can see is a goal; a blank square is not.
 */
export function badgeArt(art: BadgeArt, opts: { size?: number; earned?: boolean; className?: string } = {}): HTMLImageElement {
  const el = document.createElement('img');
  el.src = `/game/ui/badges/${art}.png`;
  el.alt = '';
  el.setAttribute('aria-hidden', 'true');
  el.draggable = false;
  const size = opts.size ?? 64;
  el.width = size;
  el.height = size;
  const earned = opts.earned ?? true;
  el.className = [
    'shrink-0 select-none transition-[filter,opacity] duration-200',
    // A locked medal is desaturated and dimmed, but NOT to the point of disappearing: the
    // first screenshot of this screen had them at 35% and greyscale, which read as damage
    // rather than as a goal. 60% with a partial desaturation keeps the shape and the tier
    // legible while leaving no doubt which ones are earned.
    earned
      ? '[filter:drop-shadow(0_2px_6px_rgba(0,0,0,0.6))]'
      : 'opacity-60 [filter:grayscale(0.75)_brightness(0.85)]',
    opts.className ?? '',
  ]
    .filter(Boolean)
    .join(' ');
  return el;
}

export type { ButtonSize, ButtonVariant, PlateChromeOptions } from './plate';

export interface ButtonOptions {
  variant?: ButtonVariant;
  /** `md` (default, unchanged) through `xl` — the menu's PLAY button is the only `xl` consumer
   * today: §3 of the visual-language spec asks for a primary that is "several times the visual
   * weight of anything else on screen", which a shared `min-h-[44px]` button can't express on
   * its own. */
  size?: ButtonSize;
  /**
   * Cuts the control's left edge into one long diagonal via `clip-path` instead of skewing.
   *
   * For PLAY only, in practice: a solid-filled face with no contrasting border loses nothing
   * to the clip, and the deeper cut reads as the bottom of the action stack's slanted wall.
   * Never put a visible border on a wedge — clip-path cuts the border off along the diagonal,
   * which is the exact defect the skew rework removed everywhere else.
   */
  wedge?: 'md' | 'lg';
  /** Parallelogram is the DEFAULT for primary/secondary/selector/danger — the reference's
   * buttons are sheared boxes, and a transform keeps the border intact on all four sides
   * (see BUTTON_BASE). Pass `false` to force a square control, e.g. inside a tight table
   * cell. Ghost buttons are never skewed: they are text, not plates. `skewX` only ever widens
   * the bounding box, so it cannot shrink a tap target below the 44px floor. */
  skewed?: boolean;
  /**
   * How hard the parallelogram leans. `md` (default) is the quiet -8° every ordinary control
   * wears; `lg` is the action stack's -14° — measured off the lobby sheet, where the mode
   * wedges and PLAY lean visibly harder than a BUY button. An option rather than a `className`
   * override because two `skew-x-*` utilities fight over one `transform`/`--tw-skew-x` and
   * stylesheet order decides — the same one-property trap as border and box-shadow.
   */
  lean?: 'md' | 'lg';
  type?: 'button' | 'submit';
  disabled?: boolean;
  className?: string;
  ariaLabel?: string;
  onClick?: (event: MouseEvent) => void;
}

/** A button that meets the tap-target and touch-action rules everywhere in the shell. */
export function button(label: string, opts: ButtonOptions = {}): HTMLButtonElement {
  const el = document.createElement('button');
  el.type = opts.type ?? 'button';
  const variant = opts.variant ?? 'secondary';
  // `ghost` is the quiet choice (Cancel, Later, an unaffordable BUY) — still a painted steel key
  // since 2026-10-12: a word with a hairline round it was the flattest control in the shell.
  plateChrome(el, label, { variant: variant === 'ghost' ? 'secondary' : variant, size: opts.size, className: opts.className });
  if (opts.ariaLabel) el.setAttribute('aria-label', opts.ariaLabel);
  if (opts.disabled) el.disabled = true;
  if (opts.onClick) el.addEventListener('click', opts.onClick);
  return el;
}

export interface CardOptions {
  className?: string;
  padded?: boolean;
  /** Draws the HUD corner brackets — reserve for a screen's one or two primary panels; on
   * every card at once it stops meaning "primary" and just becomes noise. */
  bracket?: boolean;
  /**
   * A title plate across the top (`kit-header`) with the panel's name and an optional
   * right-hand slot.
   */
  title?: string;
  /** Right-aligned content in the header strip — a count, a badge, a small control. */
  headerTrailing?: Node;
}

/**
 * A surface panel — the base container almost everything else sits inside.
 *
 * The painted armored window (`kit-window`, kit.css): a gunmetal bezel with bolts and a teal
 * light strip, the face recessed inside it. A title becomes the hazard-capped title plate
 * (`kit-header`) across the top. `bracket` is accepted for API stability: the painted bezel is
 * the "primary panel" mark now.
 */
export function card(children: (Node | string)[] = [], opts: CardOptions = {}): HTMLDivElement {
  const frame = document.createElement('div');
  frame.className = ['kit-window flex flex-col gap-3 min-w-0', opts.padded === false ? '[--kw-pad:14px]' : '', opts.className ?? '']
    .filter(Boolean)
    .join(' ');

  if (opts.title) {
    const header = document.createElement('div');
    header.className = 'kit-header [--kh:46px] justify-between -mx-1 mb-1';
    const label = document.createElement('span');
    label.className = 'kit-title font-heading uppercase text-[17px] truncate';
    label.textContent = opts.title;
    header.append(label);
    if (opts.headerTrailing) header.append(opts.headerTrailing);
    frame.append(header);
  }

  // The children's column. `gap-3` on the container rather than on each child, so a label, a
  // field and a button never sit on each other (the Account tab's Save Details sat directly on
  // the Billing Country heading under it once, 2026-09-18).
  const surface = document.createElement('div');
  surface.className = 'flex flex-col gap-3 min-h-0 h-full';
  for (const child of children) surface.append(child);
  frame.append(surface);
  return frame;
}

/**
 * A seam for a card that holds two forms: a short groove cut into the metal (`kit-well`).
 *
 * `my-1` on top of the card's own gap: a seam between two things that save separately
 * needs more air than the gap between a field and its button.
 */
export function divider(): HTMLElement {
  const el = document.createElement('div');
  el.setAttribute('role', 'separator');
  el.className = 'my-1 h-1.5 w-full kit-well';
  return el;
}

/** The art an empty state can show — either a kit icon or one of the two crate renders. */
export type EmptyStateArt = IconName | 'crate' | 'crate-open';

export interface EmptyStateOptions {
  art: EmptyStateArt;
  /** Two or three words. The screen's own name is already above this. */
  title: string;
  /** One sentence, in the second person, saying what fills this screen. */
  hint: string;
  /** The one thing to do about it. Omitted when there is nothing useful to offer. */
  action?: { label: string; onClick: () => void };
}

/**
 * The screen a player sees before they have earned anything.
 *
 * The art is the painted 3D icon at full colour, idling on a slow bob (2026-10-12): the old
 * dimmed silhouette was the flattest thing on a fresh account's screens.
 *
 * Worth a primitive rather than a sentence in a card, because on a fresh account it is
 * MOST of the shell: inventory, leaderboards, career and quests are all empty until a match
 * has been played, and four different apologetic paragraphs is what "the menus look like a
 * document" meant in practice. A drawn object, a heading and one way forward reads as a game
 * telling you what to do next.
 */
export function emptyState(opts: EmptyStateOptions): HTMLElement {
  const wrap = document.createElement('div');
  wrap.dataset.slot = 'empty-state';
  wrap.className = 'flex flex-col items-center text-center gap-3 py-10 px-4';

  const art = document.createElement('img');
  art.src =
    opts.art === 'crate' || opts.art === 'crate-open'
      ? `/game/ui/${opts.art}.png`
      : `/game/ui/icons/${opts.art}.webp`;
  art.alt = '';
  art.setAttribute('aria-hidden', 'true');
  art.draggable = false;
  art.className = 'w-24 h-24 sm:w-28 sm:h-28 select-none kit-float [filter:drop-shadow(0_8px_10px_rgba(0,0,0,0.6))]';
  wrap.append(art);

  const title = document.createElement('p');
  title.className = 'kit-title font-heading uppercase text-xl';
  title.textContent = opts.title;

  const hint = document.createElement('p');
  hint.className = 'text-[13px] text-text-dim max-w-[46ch]';
  hint.textContent = opts.hint;
  wrap.append(title, hint);

  if (opts.action) {
    wrap.append(
      button(opts.action.label, {
        variant: 'primary',
        className: 'mt-1',
        onClick: opts.action.onClick,
      }),
    );
  }
  return wrap;
}

export interface ModalOptions {
  title: string;
  content: (Node | string)[];
  onClose: () => void;
  closeLabel?: string;
  /**
   * Dialog width. `md` (default) is the form/confirmation width every existing caller wants;
   * `lg` and `xl` are for content that is a *layout* rather than a paragraph — a seven-day
   * strip crammed into 28rem is what turned the daily reward's day labels into "D…".
   */
  width?: 'md' | 'lg' | 'xl';
}

const MODAL_WIDTHS: Record<'md' | 'lg' | 'xl', string> = {
  md: 'max-w-md',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

/**
 * Appends a modal (backdrop + dialog) to `document.body` and returns a disposer.
 *
 * Closes on backdrop click, the close button, and Escape — never only on hover or a mouse-only
 * handler. The caller owns calling the returned function exactly once; it is idempotent so a
 * double-call (e.g. both a route teardown and the modal's own close button) is harmless.
 */
/**
 * How many `openModal` dialogs are currently open. A dialog owns the screen while it is up, so
 * `body.modal-open` hides the shell chrome (see `app.css`): the top bar and the mobile tab bar
 * sit under the z-50 backdrop, but a 70%-black backdrop is not opaque and their text kept
 * reading THROUGH it as if the nav were overlapping the dialog. Counted rather than a plain
 * flag, because a dialog opened from inside a dialog must not un-hide the chrome when only the
 * inner one closes.
 */
let openModals = 0;

export function openModal(opts: ModalOptions): () => void {
  const backdrop = document.createElement('div');
  // One authored entrance: the backdrop fades while the dialog rises 12px from 98% scale on an
  // exponential ease-out — the panel arrives, it does not blink into existence. CSS keyframes
  // under `motion-safe:` (see app.css), so reduced-motion gets an instant, steady dialog.
  backdrop.className =
    'fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-[3px] p-4 ' +
    'motion-safe:animate-backdrop-in';

  // The painted window `card()` uses, with its title plate across the top and the red enamel
  // close key on the plate's right end. It lands with a small overshoot (`kit-pop`).
  const dialog = document.createElement('div');
  dialog.setAttribute('role', 'dialog');
  dialog.setAttribute('aria-modal', 'true');
  dialog.setAttribute('aria-label', opts.title);
  dialog.className =
    `w-full ${MODAL_WIDTHS[opts.width ?? 'md']} max-h-[85dvh] kit-window kit-pop flex flex-col gap-3 min-h-0`;

  const shell = dialog;

  const header = document.createElement('div');
  header.className = 'kit-header [--kh:50px] justify-between gap-3 shrink-0 -mx-1';
  const heading = document.createElement('h2');
  heading.className = 'kit-title font-heading uppercase text-[18px] truncate';
  heading.textContent = opts.title;
  header.append(heading);

  let closed = false;
  const close = () => {
    if (closed) return;
    closed = true;
    openModals = Math.max(0, openModals - 1);
    if (openModals === 0) document.body.classList.remove('modal-open');
    playSample('close');
    document.removeEventListener('keydown', onKeydown);
    backdrop.remove();
    opts.onClose();
  };

  const closeLabel = opts.closeLabel ?? t('common.close');
  // The painted red enamel key with the embossed X (`kit-close`): the whole control is the art.
  // A grey × on a grey header once read as part of the header ("сливается", 2026-09-23).
  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'kit-close [--kc:40px] -mr-2';
  closeBtn.setAttribute('aria-label', closeLabel);
  closeBtn.addEventListener('click', close);
  header.append(closeBtn);
  shell.append(header);

  const body = document.createElement('div');
  body.className = 'overflow-y-auto min-h-0 px-1 pb-1';
  for (const child of opts.content) body.append(child);
  shell.append(body);

  backdrop.append(dialog);
  backdrop.addEventListener('pointerdown', (e) => {
    if (e.target === backdrop) close();
  });

  function onKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') close();
  }
  document.addEventListener('keydown', onKeydown);

  document.body.append(backdrop);
  openModals += 1;
  document.body.classList.add('modal-open');
  return close;
}

export interface ConfirmOptions {
  /** Header strip text — `t('confirmDialog.buyTitle')` and friends. */
  title: string;
  /** The question, one sentence: "Buy Nightwatch for 3,000 gems?" */
  body: string;
  /** A second, quieter line — "Replaces Vanguard." / "Balance after: 12,000". */
  detail?: string;
  /** Optional art on the left — a skin still, a weapon still, the gem icon. Sized by the caller. */
  art?: Node;
  /** Confirm button label; defaults to `t('confirmDialog.ok')`. */
  okLabel?: string;
  /** `primary` (default) for buy/equip/top-up, `danger` for anything that destroys. */
  tone?: 'primary' | 'danger';
  /** Extra content under the question — a test-mode note, a price breakdown. */
  extra?: Node[];
}

/**
 * The shell's one confirmation dialog (2026-09-11: buy, equip, top up and switch all confirm
 * first). Resolves `true` on Confirm or Enter, `false` on Cancel, Escape,
 * the × or the backdrop — never throws, never resolves twice. Built on `openModal` so it is the
 * same chassis as every other dialog, with the two plate buttons the kit sheet draws for a
 * decision: Cancel (steel, left) and the action (teal, right, focused on open so Enter is one
 * keystroke away). Callers pass a QUESTION with the concrete numbers in it — the item name and
 * the price the server will charge — because a dialog that only says "Are you sure?" is a
 * click tax, not a check.
 *
 * Test hooks: `[data-confirm="dialog"]`, `[data-confirm="ok"]`, `[data-confirm="cancel"]`.
 */
export function confirmAction(opts: ConfirmOptions): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    let settled = false;
    let close: () => void = () => {};
    const settle = (answer: boolean): void => {
      if (settled) return;
      settled = true;
      document.removeEventListener('keydown', onKeydown, true);
      close();
      resolve(answer);
    };
    // Enter confirms — captured before `openModal`'s own Escape listener so the two never race.
    function onKeydown(e: KeyboardEvent): void {
      if (e.key === 'Enter' && !e.repeat) {
        e.preventDefault();
        playSample('select');
        settle(true);
      }
    }

    const wrap = document.createElement('div');
    wrap.dataset.confirm = 'dialog';
    wrap.className = 'flex flex-col gap-4';

    const row = document.createElement('div');
    row.className = 'flex items-center gap-4';
    if (opts.art) {
      const artWrap = document.createElement('div');
      artWrap.className = 'shrink-0 flex items-center justify-center';
      artWrap.append(opts.art);
      row.append(artWrap);
    }
    const text = document.createElement('div');
    text.className = 'flex flex-col gap-1 min-w-0';
    const body = document.createElement('p');
    body.className = 'text-[15px] leading-snug text-text';
    body.textContent = opts.body;
    text.append(body);
    if (opts.detail) {
      const detail = document.createElement('p');
      detail.className = 'text-[13px] text-text-dim';
      detail.textContent = opts.detail;
      text.append(detail);
    }
    row.append(text);
    wrap.append(row);
    for (const node of opts.extra ?? []) wrap.append(node);

    const actions = document.createElement('div');
    actions.className = 'flex justify-end gap-3 pt-1';
    const cancel = button(t('common.cancel'), { variant: 'secondary', onClick: () => settle(false) });
    cancel.dataset.confirm = 'cancel';
    const ok = button(opts.okLabel ?? t('confirmDialog.ok'), {
      variant: opts.tone === 'danger' ? 'danger' : 'primary',
      onClick: () => {
        playSample('select');
        settle(true);
      },
    });
    ok.dataset.confirm = 'ok';
    actions.append(cancel, ok);
    wrap.append(actions);

    close = openModal({ title: opts.title, content: [wrap], onClose: () => settle(false) });
    document.addEventListener('keydown', onKeydown, true);
    // After the entrance frame, so focus does not fight the dialog's own animation.
    requestAnimationFrame(() => ok.focus());
  });
}

export interface TabDefinition {
  id: string;
  label: string;
  /** Optional kit icon, drawn before the label. Tabs without one are unchanged. */
  art?: IconName;
  /** Start hidden; revealed later through `TabStrip.setVisible`. */
  hidden?: boolean;
}

export interface TabStrip {
  el: HTMLElement;
  setActive(id: string): void;
  /**
   * Show or hide one tab without rebuilding the strip. A hidden tab is out of the arrow-key
   * cycle too. Built for a tab whose existence is a server-side switch the strip cannot know
   * about synchronously (Settings → Referral behind the admin's `feature.referral` toggle).
   */
  setVisible(id: string, visible: boolean): void;
}

/** A horizontal set of mutually-exclusive painted tabs — a selection widget, not routing. */
export function tabStrip(tabs: TabDefinition[], activeId: string, onSelect: (id: string) => void): TabStrip {
  const el = document.createElement('div');
  el.setAttribute('role', 'tablist');
  // Phones: one row that scrolls sideways, never a wrapped staircase — ten shop tabs on a 375px
  // screen stacked into ten rows (2026-10-12). From `md` up it wraps instead: a hidden-scrollbar
  // row has no way to reach its last tabs with a mouse, so on desktop TITLES and the eleventh
  // admin tab sat cut off at the edge, unreachable (2026-10-14). A second row is ugly; a tab
  // nobody can click is broken. Vertical padding leaves room for the lit tab's lift and glow.
  el.className =
    'flex flex-nowrap md:flex-wrap items-end gap-1.5 pt-2 pb-1 overflow-x-auto overflow-y-hidden ' +
    'md:overflow-visible [scrollbar-width:none] [&::-webkit-scrollbar]:hidden';

  const buttons = new Map<string, HTMLButtonElement>();

  function classesFor(active: boolean): string {
    // The painted trapezoid tab (`kit-tab`, kit.css): gunmetal at rest, glowing teal enamel when
    // `aria-selected` — the art swaps on the attribute, so the class list does not change with it.
    void active;
    return 'kit-tab font-heading uppercase tracking-[0.08em] text-[13px] whitespace-nowrap';
  }

  for (const tab of tabs) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.setAttribute('role', 'tab');
    if (tab.hidden) btn.hidden = true;
    // The icon is a child, so `setActive` rewriting `className` cannot drop it — the same
    // arrangement the nav uses, and for the same reason.
    if (tab.art) {
      const glyph = icon(tab.art, { size: 20 });
      glyph.dataset.tabIcon = '';
      btn.append(glyph);
    }
    const label = document.createElement('span');
    label.textContent = tab.label;
    btn.append(label);
    btn.className = classesFor(tab.id === activeId);
    // Set at CONSTRUCTION, not only inside `setActive`. Without this, a freshly rendered tab
    // strip reports no selected tab at all to assistive technology until the user clicks one —
    // and the first tab is already selected and already showing its pane.
    btn.setAttribute('aria-selected', String(tab.id === activeId));
    // Roving tabindex: the strip is one stop in the tab order and the arrow keys move within
    // it, which is what `role="tablist"` promises a keyboard user.
    btn.tabIndex = tab.id === activeId ? 0 : -1;
    btn.addEventListener('keydown', (event) => {
      const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
      if (step === 0) return;
      event.preventDefault();
      const ids = [...buttons].filter(([, entry]) => !entry.hidden).map(([id]) => id);
      const here = ids.indexOf(tab.id);
      const next = ids[(here + step + ids.length) % ids.length];
      if (next === undefined) return;
      onSelect(next);
      buttons.get(next)?.focus();
    });
    btn.addEventListener('click', () => onSelect(tab.id));
    buttons.set(tab.id, btn);
    el.append(btn);
  }

  /** Dim an inactive tab's icon a little, so the selected one is the brightest thing in the strip. */
  function paintIcon(btn: HTMLButtonElement, active: boolean): void {
    const glyph = btn.querySelector<HTMLElement>('[data-tab-icon]');
    if (glyph) glyph.style.opacity = active ? '1' : '0.7';
  }
  for (const [tabId, btn] of buttons) paintIcon(btn, tabId === activeId);

  return {
    el,
    setActive(id: string) {
      for (const [tabId, btn] of buttons) {
        btn.className = classesFor(tabId === id);
        btn.setAttribute('aria-selected', String(tabId === id));
        btn.tabIndex = tabId === id ? 0 : -1;
        paintIcon(btn, tabId === id);
      }
    },
    setVisible(id: string, visible: boolean) {
      const btn = buttons.get(id);
      if (btn) btn.hidden = !visible;
    },
  };
}

export interface CurrencyPillOptions {
  label: string;
  value: string | number;
  accent?: 'gold' | 'purple' | 'neutral';
  title?: string;
  /**
   * Drawn icon in place of the text label.
   *
   * A coin stack says "credits" faster than the word does, and it says it in the width of a
   * glyph — which is the whole problem with a currency cluster on a 375px screen. The label
   * is kept as the pill's accessible name rather than dropped.
   */
  art?: IconName;
}

/** A currency or level readout: the painted recessed slot (`kit-chip`) with the coin or gem
 * sitting over its left end and the figure in game lettering. */
export function currencyPill(opts: CurrencyPillOptions): HTMLElement {
  const accentClass =
    opts.accent === 'purple' ? 'text-purple-light' : opts.accent === 'neutral' ? 'text-text' : 'text-gold';
  const el = document.createElement('span');
  el.className = 'kit-chip [--kh:34px] text-[14px] text-text-dim';
  if (opts.title) el.title = opts.title;
  if (opts.art) {
    // The word becomes the accessible name and the picture takes its place on screen — a
    // pill reading "🪙 1,250" is the same information in half the width.
    el.setAttribute('aria-label', `${opts.label}: ${String(opts.value)}`);
    el.append(icon(opts.art, { size: 24, className: '-ml-2 [filter:drop-shadow(0_2px_2px_rgba(0,0,0,0.6))]' }));
    const value = document.createElement('span');
    value.className = `${accentClass} kit-label font-heading tabular-nums`;
    value.textContent = String(opts.value);
    el.append(value);
    return el;
  }
  el.innerHTML = `<span class="font-heading uppercase tracking-wide text-[11px]">${opts.label}</span><span class="${accentClass} font-heading font-semibold">${String(opts.value)}</span>`;
  return el;
}

export type Rarity = 'common' | 'rare' | 'epic' | 'legendary';

export interface ItemTileOptions {
  title: string;
  subtitle?: string;
  accent?: string;
  /**
   * Drawn art in place of the colour swatch.
   *
   * A picture of the thing beats a coloured square wherever there IS a picture — the swatch
   * stays the fallback for entries whose only distinguishing mark is a rarity colour.
   */
  art?: EmptyStateArt;
  /** Paints the tile's swatch (and, once selected, its border) with the matching rarity
   * gradient from `app.css` — §4/§5: "a rarity gradient scale ... is what makes a reward grid
   * look valuable rather than tabular." Omit for entries with no rarity concept yet. */
  rarity?: Rarity;
  selected?: boolean;
  locked?: boolean;
  onClick?: () => void;
}

const RARITY_CLASS: Record<Rarity, string> = {
  common: 'rarity-common border-rarity-common',
  rare: 'rarity-rare border-rarity-rare',
  epic: 'rarity-epic border-rarity-epic',
  legendary: 'rarity-legendary border-rarity-legendary',
};

/** A grid/list tile for an inventory or cosmetic entry. */
export function itemTile(opts: ItemTileOptions): HTMLElement {
  const el = document.createElement('div');
  const interactive = Boolean(opts.onClick);
  // A painted steel plate; pressable ones lift and sink (`kit-plate-hit`), the selected one glows
  // teal (`kit-selected`). The rarity colour lives on the art's swatch, not on the frame.
  el.className = [
    'kit-plate [--kp-rim:12px] [--kp-pad:12px] flex items-center gap-3',
    interactive ? 'kit-plate-hit' : '',
    opts.selected ? 'kit-selected' : '',
    opts.locked ? 'grayscale-[0.6] brightness-75' : '',
  ]
    .filter(Boolean)
    .join(' ');

  if (opts.art) {
    const art = document.createElement('img');
    art.src =
      opts.art === 'crate' || opts.art === 'crate-open'
        ? `/game/ui/${opts.art}.png`
        : `/game/ui/icons/${opts.art}.webp`;
    art.alt = '';
    art.setAttribute('aria-hidden', 'true');
    art.draggable = false;
    art.className = 'shrink-0 w-11 h-11 object-contain select-none [filter:drop-shadow(0_3px_3px_rgba(0,0,0,0.6))]';
    el.append(art);
  } else {
    const swatch = document.createElement('span');
    swatch.className = ['shrink-0 w-10 h-10 rounded-md border-2 border-black/70 shadow-[inset_0_2px_0_rgba(255,255,255,0.35),0_2px_3px_rgba(0,0,0,0.6)]', opts.rarity ? RARITY_CLASS[opts.rarity] : ''].filter(Boolean).join(' ');
    if (opts.accent) swatch.style.background = opts.accent;
    el.append(swatch);
  }

  const text = document.createElement('span');
  text.className = 'flex-1 min-w-0';
  text.innerHTML = `
    <span class="block kit-label font-heading uppercase text-[15px] truncate">${opts.title}</span>
    ${opts.subtitle ? `<span class="block text-[12px] text-text-dim truncate">${opts.subtitle}</span>` : ''}
  `;
  el.append(text);

  if (interactive) {
    el.setAttribute('role', 'button');
    el.tabIndex = 0;
    el.addEventListener('click', () => opts.onClick?.());
    el.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        opts.onClick?.();
      }
    });
  }

  return el;
}

/** A label/value row — stat blocks, settings rows, profile summaries. Both sides sit in the
 * display face: the label is a stat label (condensed, uppercase, dim), the value is the
 * number that matters (condensed, bright) — a plain word and a plain digit next to each other
 * was exactly the "half-converted" mixture that read as accidental. */
export function statRow(label: string, value: string | number): HTMLElement {
  const el = document.createElement('div');
  el.className = 'kit-well flex items-center justify-between gap-4 min-h-[40px] px-3 py-2';
  el.innerHTML = `<span class="font-heading uppercase tracking-wide text-[12px] text-text-dim">${label}</span><span class="kit-label font-heading text-base tabular-nums">${String(value)}</span>`;
  return el;
}

export type BadgeTone = 'neutral' | 'gold' | 'success' | 'error';

const BADGE_TONES: Record<BadgeTone, string> = {
  neutral: 'text-text',
  gold: 'text-gold',
  success: 'text-success',
  error: 'text-error',
};

/** A small status label — quest state, purchase result, match outcome: the painted slot with
 * the word in the tone's colour, ink-outlined. */
export function badge(text: string, tone: BadgeTone = 'neutral'): HTMLElement {
  const el = document.createElement('span');
  el.className = `kit-chip [--kh:26px] text-[11px] font-heading uppercase tracking-[0.08em] [-webkit-text-stroke:0.12em_var(--kit-ink)] [paint-order:stroke_fill] ${BADGE_TONES[tone]}`;
  el.textContent = text;
  return el;
}

export interface ProgressBarOptions {
  value: number;
  max: number;
  /** Screen-reader label; the bar itself is decorative. */
  label?: string;
  /**
   * `gold` (default) for a measured statistic, `cyan` for progress toward a level.
   *
   * They differ because gold is the currency/readout hue everywhere in this shell, so a gold XP
   * bar next to a gold credit balance reads as money.
   */
  accent?: 'gold' | 'cyan';
}

/** A bounded progress indicator — quest progress, XP-to-next-level, anything counted toward a
 * target. Clamps visually; callers pass the raw numbers straight through (e.g. `progress` /
 * `targetCount` from the server) rather than pre-dividing them. The fill stays the track's
 * first (and only) child — some callers reach into `firstElementChild`.
 *
 * The painted machined groove (`kit-bar`) with a glossy enamel fill cut into cells and a lit
 * leading edge (`kit-bar-fill`); gold for a statistic, teal for progress toward a level. */
export function progressBar(opts: ProgressBarOptions): HTMLElement {
  const pct = opts.max > 0 ? Math.min(100, Math.max(0, (opts.value / opts.max) * 100)) : 0;
  const el = document.createElement('div');
  el.setAttribute('role', 'progressbar');
  el.setAttribute('aria-valuemin', '0');
  el.setAttribute('aria-valuemax', String(opts.max));
  el.setAttribute('aria-valuenow', String(opts.value));
  if (opts.label) el.setAttribute('aria-label', opts.label);
  el.className = 'kit-bar [--kh:22px] w-full';
  const fill = document.createElement('div');
  fill.className = 'kit-bar-fill';
  if (opts.accent !== 'cyan') fill.dataset.tone = 'gold';
  fill.style.width = `${pct}%`;
  el.append(fill);
  return el;
}

/**
 * A section heading.
 *
 * `h1` is the SCREEN TITLE: huge game lettering with the ink outline (`kit-title`) — "STORE",
 * "INVENTORY" stamped over the scene. `h2` is a sub-section label in the same lettering, smaller
 * (a stats table's "Recent matches" is not a screen title and must not shout like one).
 *
 * `trailing` is the inline slot the reference puts screen-local tabs in ("INVENTORY ·
 * ALL WEAPONS · SPRAYS" beside the title) — appended after the heading, baseline-aligned.
 * Signature stays backward compatible: every existing `sectionHeading('X')` call renders.
 */
export function sectionHeading(
  text: string,
  opts: { level?: 'h1' | 'h2'; className?: string; trailing?: Node } = {},
): HTMLElement {
  const wrapper = document.createElement('div');
  const level = opts.level ?? 'h1';
  wrapper.className = [level === 'h1' ? 'mb-6' : 'mb-5', opts.className ?? ''].filter(Boolean).join(' ');

  const line = document.createElement('div');
  // An `h2` with something trailing is a heading ROW — the trailing control sits at its right
  // end, not stacked underneath it. Appended below the heading, a "View all" link read as a
  // stray sentence dangling under the section title rather than as the section's own control.
  line.className =
    level === 'h1'
      ? 'flex flex-wrap items-baseline gap-x-6 gap-y-2'
      : opts.trailing
        ? 'flex items-center justify-between gap-3'
        : '';

  const heading = document.createElement(level);
  heading.className =
    level === 'h2'
      ? 'kit-title text-xl font-heading uppercase'
      : 'kit-title text-[30px] sm:text-4xl md:text-5xl font-heading uppercase [overflow-wrap:anywhere]';
  heading.textContent = text;
  line.append(heading);
  if (opts.trailing) line.append(opts.trailing);
  wrapper.append(line);

  return wrapper;
}

export interface StatTileOptions {
  label: string;
  value: string | number;
  /** A second line under the value — a delta, a rank name, a denominator. */
  hint?: string;
  art?: IconName;
  accent?: 'default' | 'gold' | 'cyan';
  /**
   * `md` (default) is the inline figure — a dossier stat, a shop card's price.
   * `lg` is the reference's HERO figure: the 3x3 career grid and the loadout's derived numbers,
   * where the number is the content of the tile and not an annotation beside a label.
   */
  size?: 'md' | 'lg';
  /** Centres label and value. The reference centres a figure whenever the tile is in a grid of
   * them — a column of left-aligned numerals in boxes of differing width reads as a table. */
  centred?: boolean;
}

/**
 * One number, framed.
 *
 * The shell had exactly one way to show a statistic — `statRow`, a label and a value on one
 * line with a hairline under it — which is a table row, and a screen made of table rows is a
 * document. A career or a match summary needs figures that read at a glance and at a distance,
 * so this is the block form: the number large in the display face, the label small and tracked
 * out above it, on the same recessed face every other surface uses.
 */
export function statTile(opts: StatTileOptions): HTMLElement {
  const el = document.createElement('div');
  el.dataset.slot = 'stat-tile';
  const big = opts.size === 'lg';
  // The painted steel plate; the figure is game lettering, the icon full colour.
  el.className = 'kit-plate [--kp-rim:12px] min-w-0 ' + (big ? '[--kp-pad:16px_14px]' : '[--kp-pad:10px_12px]');

  const label = document.createElement('span');
  label.className =
    'flex items-center gap-1.5 font-heading uppercase tracking-[0.04em] text-[10px] text-text-dim ' +
    (opts.centred ? 'justify-center' : '');
  if (opts.art) label.append(icon(opts.art, { size: big ? 22 : 18 }));
  // Wraps rather than truncates: three tiles across a 392px dossier left "K/D RA…" (2026-10-12).
  const labelText = document.createElement('span');
  labelText.className = 'leading-tight min-w-0';
  labelText.textContent = opts.label;
  label.append(labelText);

  const value = document.createElement('span');
  const tone =
    opts.accent === 'gold' ? 'text-gold' : opts.accent === 'cyan' ? 'text-cyan' : 'text-text';
  value.className =
    `block kit-label font-heading leading-none tabular-nums ${tone} ` +
    (big ? 'text-[34px] mt-2.5 ' : 'text-[22px] mt-1.5 ') +
    (opts.centred ? 'text-center' : '');
  value.textContent = String(opts.value);

  el.append(label, value);
  if (opts.hint) {
    const hint = document.createElement('span');
    hint.className = 'block text-[11px] text-text-dim mt-1 truncate';
    hint.textContent = opts.hint;
    el.append(hint);
  }
  return el;
}

/**
 * The sign-out control, exported for the Settings screen (§2b: sign-out lives in Settings,
 * never in the chrome — the reference has no account controls floating over the scene).
 * A factory rather than markup in settings-route so the label/variant stays one decision.
 */
export function signOutButton(onLogout: () => void): HTMLButtonElement {
  return button(t('common.signOut'), {
    variant: 'danger',
    className: 'px-6',
    ariaLabel: t('common.signOut'),
    onClick: onLogout,
  });
}

/**
 * A `<select>` wearing the shell's plate, with a list that opens DOWNWARD.
 *
 * Why it exists: a native select's popup direction belongs to the browser. Chrome opens it
 * wherever it fits, so the billing-country field — which sits low in the Account card — dropped
 * its 27 countries UPWARD, over the fields above it (reported 2026-09-18). Nothing in CSS can
 * ask it not to; the only way to own the direction is to own the list.
 *
 * The native element STAYS, hidden, as the state: `select.value` keeps working, so do the
 * `data-settings` hooks, the `change` listeners and every test that reads a value. This
 * function only replaces the *control*, and it syncs both ways — a `change` fired on the select
 * from anywhere repaints the plate.
 *
 * The keyboard contract is the one `i18n/lang-pick.ts` implements, plus type-ahead: with 27
 * options, losing the native "press L for Latvia" would be a real regression.
 *
 * Returns the element to place in the layout; keep the select for reading the value.
 */
export function plateSelect(select: HTMLSelectElement): HTMLElement {
  const root = document.createElement('div');
  root.className = 'relative w-full';

  select.classList.add('sr-only');
  select.tabIndex = -1;
  select.setAttribute('aria-hidden', 'true');

  const trigger = document.createElement('button');
  trigger.type = 'button';
  trigger.className =
    'kit-well min-h-[44px] w-full text-text text-sm px-3 '
    + 'flex items-center justify-between gap-2 text-left touch-manipulation cursor-pointer '
    + 'hover:brightness-125 focus-visible:outline focus-visible:outline-cyan';
  trigger.setAttribute('aria-haspopup', 'listbox');
  trigger.setAttribute('aria-expanded', 'false');

  const label = document.createElement('span');
  label.className = 'truncate';
  const caret = document.createElement('span');
  caret.setAttribute('aria-hidden', 'true');
  caret.className = 'shrink-0 rotate-90';
  caret.append(icon('next', { size: 18 }));
  trigger.append(label, caret);

  const menu = document.createElement('div');
  menu.setAttribute('role', 'listbox');
  menu.hidden = true;
  // `top-[calc(100%+4px)]`, never `bottom-full`: down is the whole point. If the page cannot
  // show it, the page scrolls (see `open()`), because a list that moves to wherever it fits is
  // exactly the behaviour being replaced here.
  menu.className =
    'absolute left-0 right-0 top-[calc(100%+4px)] z-40 max-h-[280px] overflow-y-auto '
    + 'kit-window [--kw-rim:16px] [--kw-pad:8px] flex flex-col';

  const options = [...select.options].map((option) => {
    const row = document.createElement('div');
    row.setAttribute('role', 'option');
    row.tabIndex = -1;
    row.dataset.value = option.value;
    row.textContent = option.textContent;
    row.className = 'min-h-[44px] px-3 flex items-center text-sm cursor-pointer touch-manipulation';
    menu.append(row);
    return row;
  });

  root.append(select, trigger, menu);

  let open = false;

  const paint = (): void => {
    const current = select.options[select.selectedIndex];
    label.textContent = current?.textContent ?? '';
    for (const row of options) {
      const selected = row.dataset.value === select.value;
      row.setAttribute('aria-selected', String(selected));
      row.className =
        'min-h-[44px] px-3 flex items-center text-sm cursor-pointer touch-manipulation '
        + (selected ? 'text-cyan bg-white/8' : 'text-text-dim hover:text-white hover:bg-white/5');
    }
  };

  // Bound only while the list is open, and unbound when it closes — this control has no
  // disposer, and a document listener per mounted select would outlive every route change.
  const onOutside = (event: PointerEvent): void => {
    if (!root.contains(event.target as Node)) setOpen(false);
  };

  const setOpen = (next: boolean): void => {
    open = next;
    menu.hidden = !next;
    trigger.setAttribute('aria-expanded', String(next));
    if (!next) {
      document.removeEventListener('pointerdown', onOutside);
      return;
    }
    document.addEventListener('pointerdown', onOutside);
    const selected = options.find((row) => row.dataset.value === select.value) ?? options[0];
    selected?.focus();
    // Down first, and scrolled into view only if the viewport truly cannot show it.
    if (menu.getBoundingClientRect().bottom > window.innerHeight) {
      menu.scrollIntoView({ block: 'nearest' });
    }
  };

  const choose = (row: HTMLElement): void => {
    select.value = row.dataset.value ?? '';
    select.dispatchEvent(new Event('change', { bubbles: true }));
    paint();
    setOpen(false);
    trigger.focus();
  };

  const move = (delta: number): void => {
    const index = options.findIndex((row) => row === document.activeElement);
    const from = index >= 0 ? index : options.findIndex((row) => row.dataset.value === select.value);
    options[(Math.max(0, from) + delta + options.length) % options.length]?.focus();
  };

  /** Type-ahead, the one thing a native select does that a listbox has to be told to do. */
  let typed = '';
  let typedAt = 0;
  const jumpTo = (key: string): void => {
    const now = Date.now();
    typed = now - typedAt > 800 ? key : typed + key;
    typedAt = now;
    const match = options.find((row) => (row.textContent ?? '').toLowerCase().startsWith(typed));
    match?.focus();
  };

  trigger.addEventListener('click', () => setOpen(!open));
  trigger.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setOpen(true);
    }
  });
  menu.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') { event.preventDefault(); setOpen(false); trigger.focus(); return; }
    if (event.key === 'ArrowDown') { event.preventDefault(); move(1); return; }
    if (event.key === 'ArrowUp') { event.preventDefault(); move(-1); return; }
    if (event.key === 'Home') { event.preventDefault(); options[0]?.focus(); return; }
    if (event.key === 'End') { event.preventDefault(); options[options.length - 1]?.focus(); return; }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      const row = options.find((candidate) => candidate === document.activeElement);
      if (row) choose(row);
      return;
    }
    if (event.key.length === 1) jumpTo(event.key.toLowerCase());
  });
  for (const row of options) row.addEventListener('click', () => choose(row));
  // Something else set the value (a fetched account, a test): the plate follows it.
  select.addEventListener('change', paint);

  paint();
  return root;
}
